import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Group,
  Text,
  Button,
  Loader,
  Alert,
  Stack,
  Paper,
  Title,
  Badge,
  Progress,
  Anchor,
  UnstyledButton,
  Modal,
  TextInput,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconChevronDown,
  IconDeviceFloppy,
  IconBarcode,
  IconPrinter,
  IconLockOpen,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import { useAuth } from "../../hooks/useAuth";
import { SpecimenInsufficientTissueCheckbox } from "../../components/grossing/SpecimenInsufficientTissueCheckbox";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  useCaseForGrossing,
  useStartGrossing,
  useUpdateSpecimenGrossing,
  useCompleteGrossing,
  useGrossingQueue,
  useCaseLock,
  useReleaseLock,
  useForceReleaseLock,
  useExtendLock,
  usePrintBlockLabels,
  useOpenCaseByBarcode,
} from "../../hooks/useWorkflows";
import {
  useSourcesWithSubsites,
  useCollectionMethods,
} from "../../hooks/useReferenceData";
import {
  BlockManagementCard,
  type GrossTemplateSelectorHandle,
  UnsavedChangesModal,
  GrossingCaseSummary,
  GrossingRequisitionCard,
  SpecimenGrossingFields,
} from "../../components/grossing";
import {
  isCompleteGrossingBarcode,
  useBarcodeScanner,
} from "../../hooks/useBarcodeScanner";
import { CaseNotesSection } from "../../components/accessioning/CaseNotesSection";
import { CaseFlagsSection } from "../../components/cases/CaseFlagsSection";
import { CaseCommentsSection } from "../../components/accessioning/CaseCommentsSection";
import ErrorsCard from "../../components/accessioning/ErrorsCard";
import { CassettePrinterStatusPanel } from "../../components/workflows";
import { useCassettePrint } from "../../hooks/useCassettePrint";
import {
  StickerPrinterStatusPanel,
  ReceivedExternalPassThroughPanel,
} from "../../components/sticker";
import { useStickerPrinterStatus } from "../../hooks/useStickerPrinterStatus";
import {
  stickerLabelPrinterService,
  type StickerLabelData,
} from "../../services/stickerLabelPrinter.service";
import { workstationPreferenceService } from "../../services/workstationPreference.service";
import type { CaseBlock, CaseSpecimen } from "../../services/cases.service";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import {
  applyFragmentCountPatchToSpecimen,
  type FragmentCountPatch,
  type FragmentCountQualifier,
} from "../../utils/fragmentCount";
import { shouldShowLegacyScannerControls } from "../../utils/scannerControls";
import { requireDateOnlyString } from "../../utils/dateOnly";

interface GrossingBlockStickerLabelInput {
  block: CaseBlock;
  caseId: string;
  caseNumber: string;
  dateReceived: unknown;
  patientName: string;
  specimenNumber: string;
}

export function buildGrossingBlockStickerLabel({
  block,
  caseId,
  caseNumber,
  dateReceived,
  patientName,
  specimenNumber,
}: GrossingBlockStickerLabelInput): StickerLabelData {
  const blockNumber = block.number ?? "";
  return {
    itemSlotId: `${Date.now()}-b-${block.id}`,
    itemType: "block",
    caseId,
    itemId: block.id,
    caseNumber,
    patientName,
    specimen: specimenNumber,
    blockId: blockNumber,
    slideId: "",
    stain: "",
    dateReceived: requireDateOnlyString(dateReceived, "Date received"),
    barcodeValue: `${caseNumber};${specimenNumber};${blockNumber}`,
    isReprint: true,
  };
}

export default function GrossingDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const showLegacyScannerControls = shouldShowLegacyScannerControls();
  const { user } = useAuth();
  const userId = user?.id;

  // State
  const [selectedSpecimenId, setSelectedSpecimenId] = useState<string>("");
  const [specimenUpdates, setSpecimenUpdates] = useState<
    Record<
      string,
      {
        gross?: string;
        internalGrossCodeId?: string;
        sourceId?: string;
        subsiteId?: string;
        collectionMethod?: string;
        fragmentCount?: number | null;
        fragmentCountQualifier?: FragmentCountQualifier | null;
      }
    >
  >({});
  const [
    unsavedModalOpened,
    { open: openUnsavedModal, close: closeUnsavedModal },
  ] = useDisclosure(false);
  const [
    cancelModalOpened,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    url: string;
    specimenLetter?: string;
  } | null>(null);
  // State to track validation errors for fields
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  // Left sidebar scroll indicator state
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  // Backup barcode scanner state
  const [scanInput, setScanInput] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  // Ref for scrolling to errors section
  const errorsRef = useRef<HTMLDivElement>(null);
  // Ref for scrolling to top of form area
  const formScrollRef = useRef<HTMLDivElement>(null);
  // Refs for form fields (for scrolling to errors)
  const sourceFieldRef = useRef<HTMLDivElement>(null);
  const collectionMethodFieldRef = useRef<HTMLDivElement>(null);
  const grossFieldRef = useRef<HTMLDivElement>(null);
  const grossTemplateSelectorRef = useRef<GrossTemplateSelectorHandle>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  // Refs for specimen list items (for auto-scrolling)
  const specimenRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Track if we've already started grossing to prevent infinite loops
  const hasStartedGrossing = useRef(false);
  // Debounce ref for keyboard specimen navigation to prevent rapid state desync
  const navDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state when navigating to a different case
  useEffect(() => {
    hasStartedGrossing.current = false;
    setSelectedSpecimenId("");
    setSpecimenUpdates({});
    setFieldErrors({});
  }, [caseId]);

  // Queries
  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
    refetch: refetchCase,
  } = useCaseForGrossing(caseId);
  const { data: sourcesData } = useSourcesWithSubsites();
  const { data: collectionMethods = [] } = useCollectionMethods();
  const { data: grossingQueue = [] } = useGrossingQueue();

  // Extract sources for auto-fill logic
  const sources = sourcesData?.sources || [];

  // Lock query
  const { data: lockData } = useCaseLock(caseId);

  // Mutations
  const startGrossingMutation = useStartGrossing();
  const updateSpecimenMutation = useUpdateSpecimenGrossing();
  const completeGrossingMutation = useCompleteGrossing();
  const releaseLockMutation = useReleaseLock();
  const extendLockMutation = useExtendLock();
  const printLabelsMutation = usePrintBlockLabels();
  const { printLabels } = useCassettePrint();
  const openCaseMutation = useOpenCaseByBarcode();

  // Get all blocks for the case (from caseData.specimens[].blocks)
  const allBlocks = (caseData?.specimens || []).flatMap(
    (specimen: any) => specimen.blocks || []
  );

  // Check if locked by another user
  const isLockedByOther =
    lockData && lockData.lockedById && lockData.lockedById !== userId;

  const canForceRelease = useHasPermission("case_locks.force_release");
  const forceReleaseMutation = useForceReleaseLock();

  const { data: stickerPrinterStatus } = useStickerPrinterStatus();

  const handleReprintSticker = useCallback(
    async (block: CaseBlock) => {
      const workstationId =
        workstationPreferenceService.getSelectedWorkstationId();
      if (!workstationId || !stickerPrinterStatus?.deviceId) {
        notifications.show({
          title: "Print Failed",
          message: "Printer or workstation not ready.",
          color: "red",
          autoClose: 4000,
        });
        return;
      }

      if (stickerPrinterStatus.status !== "connected") {
        notifications.show({
          title: "Print Failed",
          message: "Printer is offline or in error state.",
          color: "red",
          autoClose: 4000,
        });
        return;
      }

      const specimen = (caseData?.specimens || []).find(
        (s: CaseSpecimen) => s.id === block.specimenId
      );
      if (!caseData || !specimen) {
        notifications.show({
          title: "Print Failed",
          message: "Case data not ready.",
          color: "red",
          autoClose: 4000,
        });
        return;
      }

      const patientName =
        resolvePatientLabelName(caseData.patient, caseData.draftPatientData) ??
        "";
      const specimenNum = specimen.number ?? "";

      try {
        const label = buildGrossingBlockStickerLabel({
          block,
          caseId: caseId!,
          caseNumber: caseData.caseNumber,
          dateReceived: caseData.dateReceived,
          patientName,
          specimenNumber: specimenNum,
        });
        const result = await stickerLabelPrinterService.printStickerLabels(
          [label],
          stickerPrinterStatus.deviceId,
          workstationId
        );
        if (result.success) {
          notifications.show({
            title: "Reprint Dispatched",
            message: `Sticker for ${specimenNum}${label.blockId} sent via ${result.method} bridge.`,
            color: "green",
            autoClose: 4000,
          });
        } else {
          notifications.show({
            title: "Reprint Failed",
            message: result.error || "Could not send sticker to printer.",
            color: "red",
            autoClose: 5000,
          });
        }
      } catch (err: any) {
        notifications.show({
          title: "Reprint Failed",
          message: err.message || "An unexpected error occurred.",
          color: "red",
          autoClose: 5000,
        });
      }
    },
    [caseId, caseData, stickerPrinterStatus]
  );

  const handleForceUnlock = useCallback(() => {
    if (
      !window.confirm(
        `Force-unlock this case from ${
          lockData?.lockedByName || "another user"
        }? They will lose their lock immediately.`
      )
    )
      return;
    forceReleaseMutation.mutate(caseId!, {
      onSuccess: () => {
        notifications.show({
          title: "Lock Released",
          message: "The case has been unlocked.",
          color: "green",
        });
      },
      onError: (err: Error) => {
        notifications.show({
          title: "Error",
          message: err.message || "Failed to release lock",
          color: "red",
        });
      },
    });
  }, [caseId, lockData, forceReleaseMutation]);

  // Auto-start grossing on mount (only if not locked by another user)
  useEffect(() => {
    // Only start grossing once - prevent infinite loops from dependency changes
    if (hasStartedGrossing.current) return;

    if (caseId && userId && caseData && !lockData && !isLockedByOther) {
      hasStartedGrossing.current = true;
      startGrossingMutation.mutate(caseId, {
        onError: (error: any) => {
          // Reset the flag so user can retry if needed
          hasStartedGrossing.current = false;
          notifications.show({
            title: "Error",
            message: error.message || "Failed to start grossing",
            color: "red",
          });
          // Navigate back to queue if lock acquisition failed
          navigate("/grossing");
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, userId, caseData, lockData, isLockedByOther]);

  // Extend lock every 2 minutes (TTL is 5 min — heartbeat keeps it alive while on page)
  useEffect(() => {
    if (!caseId || !userId || !lockData || isLockedByOther) return;

    const interval = setInterval(() => {
      extendLockMutation.mutate(caseId);
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, userId, lockData, isLockedByOther]);

  // Normalize gross text: strip HTML, decode entities, collapse whitespace
  const normalizeGrossText = (value: string): string => {
    if (!value) return "";
    return value
      .replace(/<[^>]*>/g, "") // Remove all HTML tags
      .replace(/&nbsp;/g, " ") // Decode common entities
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\s+/g, " ") // Collapse all whitespace
      .trim();
  };

  // Detect unresolved template variables: {{...}} not wrapped in a data-variable span
  const hasUnresolvedTemplateVars = (html: string): boolean => {
    const allVars = html.match(/\{\{[^}]+\}\}/g) || [];
    const wrappedVars = (
      html.match(
        /<span[^>]*data-variable="[^"]*"[^>]*>\{\{[^}]+\}\}<\/span>/g
      ) || []
    ).length;
    return allVars.length > wrappedVars;
  };

  // Helper to check if a specimen has all required fields filled
  // (inline validation to avoid dependency issues)
  const checkSpecimenComplete = (specimen: any): boolean => {
    const updates = specimenUpdates[specimen.id] || {};
    const gross = updates.gross ?? specimen.gross ?? "";
    const sourceId = updates.sourceId ?? specimen.sourceId;
    const collectionMethod =
      updates.collectionMethod ?? specimen.collectionMethod;

    const normalizedGross = normalizeGrossText(gross);

    return !!(
      sourceId &&
      collectionMethod &&
      normalizedGross.length >= 5 &&
      !hasUnresolvedTemplateVars(gross)
    );
  };

  // Auto-select first specimen on mount (sorted alphabetically)
  // If navigated from a barcode scan, pre-select the scanned specimen letter.
  useEffect(() => {
    // Also re-run selection when the stored ID no longer exists in the current
    // specimen list.  This happens when the user edits accessioning after
    // starting grossing: the backend deletes and recreates specimens (new UUIDs),
    // so a previously selected ID becomes stale after the next refetch.
    const selectedIdStillExists = caseData?.specimens?.some(
      (s: any) => s.id === selectedSpecimenId
    );
    if (
      caseData?.specimens?.length > 0 &&
      (!selectedSpecimenId || !selectedIdStillExists)
    ) {
      // Sort specimens alphabetically before selecting
      const sortedSpecimens = [...caseData.specimens].sort((a: any, b: any) =>
        (a.number || "").localeCompare(b.number || "")
      );

      // Check if a specimen letter was passed via router state (barcode scan)
      const preselectedLetter = (
        location.state as { specimenLetter?: string } | null
      )?.specimenLetter;
      if (preselectedLetter) {
        const match = sortedSpecimens.find(
          (s: any) =>
            s.number?.toUpperCase() === preselectedLetter.toUpperCase()
        );
        if (match) {
          setSelectedSpecimenId(match.id);
          return;
        }
        // Show error — scanned specimen doesn't exist in this case
        notifications.show({
          title: "Specimen Not Found",
          message: `Specimen "${preselectedLetter}" not found in this case. Available: ${sortedSpecimens
            .map((s: any) => s.number)
            .join(", ")}`,
          color: "orange",
          autoClose: 6000,
        });
      }

      const firstIncomplete = sortedSpecimens.find(
        (s: any) => !checkSpecimenComplete(s)
      );
      if (firstIncomplete) {
        setSelectedSpecimenId(firstIncomplete.id);
      } else {
        setSelectedSpecimenId(sortedSpecimens[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData, selectedSpecimenId]);

  // Release lock on unmount
  useEffect(() => {
    return () => {
      if (caseId) {
        releaseLockMutation.mutate(caseId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Safety net: use fetch with keepalive=true so the request survives tab close/browser back.
  // This runs on every caseId change and registers a beforeunload handler.
  useEffect(() => {
    if (!caseId) return;

    const handleBeforeUnload = () => {
      const token = localStorage.getItem("token");
      const apiBase = `${process.env.REACT_APP_API_BASE_URL || ""}/lis`;
      fetch(`${apiBase}/workflows/locks/${caseId}`, {
        method: "DELETE",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [caseId]);

  // Clear field errors when specimen changes
  useEffect(() => {
    setFieldErrors({});
  }, [selectedSpecimenId]);

  // Auto-scroll to selected specimen in the left tab strip
  useEffect(() => {
    if (selectedSpecimenId && specimenRefs.current[selectedSpecimenId]) {
      specimenRefs.current[selectedSpecimenId]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedSpecimenId]);

  // Auto-focus the gross template editor when specimen changes
  useEffect(() => {
    if (!selectedSpecimenId) return;
    const timer = setTimeout(() => {
      grossTemplateSelectorRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedSpecimenId]);

  // Check if left sidebar needs scroll indicator
  useEffect(() => {
    const checkScrollable = () => {
      const scrollContainer = document.querySelector(".left-sidebar-scroll");
      if (scrollContainer) {
        const hasScroll =
          scrollContainer.scrollHeight > scrollContainer.clientHeight;
        const isAtBottom =
          scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - 10;
        setShowScrollIndicator(hasScroll && !isAtBottom);
      }
    };

    // Check initially and on scroll/resize
    checkScrollable();
    const scrollContainer = document.querySelector(".left-sidebar-scroll");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollable);
    }
    window.addEventListener("resize", checkScrollable);

    // Re-check periodically for content changes (e.g., collapsing cards)
    const interval = setInterval(checkScrollable, 500);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScrollable);
      }
      window.removeEventListener("resize", checkScrollable);
      clearInterval(interval);
    };
  }, []);

  // Handlers
  const hasUnsavedChanges = Object.keys(specimenUpdates).length > 0;

  // Barcode scanner handler for scanning while on detail page
  const handleBarcodeScan = useCallback(
    async (barcode: string) => {
      try {
        const scannedCase = await openCaseMutation.mutateAsync({
          barcode,
          intent: "grossing",
        });
        const specimenLetter = barcode.split(";")[1]?.trim();

        if (scannedCase.id !== caseId) {
          notifications.show({
            title: "Different Case",
            message:
              "Return to the Grossing Queue before scanning another case",
            color: "red",
          });
          return;
        }

        const matchingSpecimen = caseData?.specimens?.find(
          (specimen: any) =>
            specimen.number?.toUpperCase() === specimenLetter?.toUpperCase()
        );
        if (!matchingSpecimen) {
          notifications.show({
            title: "Barcode Error",
            message: "The scanned specimen does not belong to this case",
            color: "red",
          });
          return;
        }
        setSelectedSpecimenId(matchingSpecimen.id);
      } catch (err: any) {
        notifications.show({
          title: "Barcode Error",
          message: err.message || "Failed to open scanned case",
          color: "red",
        });
      }
    },
    [openCaseMutation, caseId, caseData?.specimens]
  );

  const handleManualBarcodeScan = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;
      const value = scanInput.trim();
      if (!value) return;
      setScanError(null);
      try {
        await handleBarcodeScan(value);
      } catch {
        // errors are handled inside handleBarcodeScan via notifications
      }
      // Always clear the input after submission (whether success or error)
      setScanInput("");
    },
    [scanInput, handleBarcodeScan]
  );

  useBarcodeScanner(handleBarcodeScan, {
    enabled: !openCaseMutation.isPending,
    ownerKey: `grossing-detail:${caseId ?? "loading"}`,
    isCompleteBarcode: isCompleteGrossingBarcode,
  });

  const handleFieldChange = (
    specimenId: string,
    field: string,
    value: string | number | null
  ) => {
    setSpecimenUpdates((prev) => ({
      ...prev,
      [specimenId]: {
        ...prev[specimenId],
        [field]: value,
      },
    }));
  };

  const handleFragmentCountChange = useCallback(
    (specimenId: string, patch: FragmentCountPatch) => {
      setSpecimenUpdates((previous) =>
        applyFragmentCountPatchToSpecimen(previous, specimenId, patch)
      );
    },
    []
  );

  const handleSelectedSpecimenFragmentCountChange = useCallback(
    (patch: FragmentCountPatch) => {
      if (!selectedSpecimenId) return;
      handleFragmentCountChange(selectedSpecimenId, patch);
    },
    [handleFragmentCountChange, selectedSpecimenId]
  );

  const handleSaveProgress = useCallback(
    async (autoAdvance = true) => {
      if (!userId) return;

      try {
        // Force-flush the active TipTap editor for the currently-visible
        // specimen. Without this, a user who types and immediately clicks
        // Save All (without blurring the editor) can hit a state-commit race
        // where specimenUpdates still holds the pre-typed value. Most often
        // observed on the LAST specimen, where there's no specimen-switch
        // to trigger the editor's onUpdate flush as a side effect.
        const flushedGross =
          grossTemplateSelectorRef.current?.flushPendingContent?.() ?? null;
        const mergedUpdates = { ...specimenUpdates };
        if (flushedGross != null && selectedSpecimenId) {
          const existing = mergedUpdates[selectedSpecimenId] || {};
          if (existing.gross !== flushedGross) {
            mergedUpdates[selectedSpecimenId] = {
              ...existing,
              gross: flushedGross,
            };
          }
        }

        const updates = Object.entries(mergedUpdates).filter(
          ([, updates]) => Object.keys(updates).length > 0
        );

        await Promise.all(
          updates.map(([specimenId, updateData]) =>
            updateSpecimenMutation.mutateAsync({
              caseId: caseId!,
              specimenId,
              data: {
                ...updateData,
                tissueDisposition: "entire_sample",
              },
            })
          )
        );

        notifications.show({
          title: "Progress Saved",
          message: "All changes have been saved",
          color: "green",
        });

        // Only clear the specimens that were included in THIS save batch.
        // Clearing all specimenUpdates would wipe any content the user typed
        // into the currently-visible specimen while the background save was
        // running (e.g. Save All fires save for A in the background, advances
        // to B; user starts typing in B; save completes and clears B's content).
        const savedIds = new Set(updates.map(([id]) => id));
        setSpecimenUpdates((prev) => {
          const next = { ...prev };
          savedIds.forEach((id) => delete next[id]);
          return next;
        });
        // Await refetch so fresh specimen data is loaded before advancing
        await refetchCase();

        // Auto-advance to next specimen after successful save
        if (autoAdvance && selectedSpecimenId && caseData?.specimens) {
          const sortedSpecimens = [...caseData.specimens].sort(
            (a: any, b: any) => (a.number || "").localeCompare(b.number || "")
          );
          const currentIndex = sortedSpecimens.findIndex(
            (s: any) => s.id === selectedSpecimenId
          );
          if (currentIndex >= 0 && currentIndex < sortedSpecimens.length - 1) {
            // Move to next specimen
            setSelectedSpecimenId(sortedSpecimens[currentIndex + 1].id);
          }
        }
      } catch (error: any) {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to save progress",
          color: "red",
        });
      }
    },
    [
      userId,
      specimenUpdates,
      updateSpecimenMutation,
      caseId,
      refetchCase,
      selectedSpecimenId,
      caseData?.specimens,
    ]
  );

  // Save current specimen and immediately advance to the next one.
  // Captures a snapshot of current data, fires mutations in the background,
  // clears local state, and navigates — all without waiting for the network.
  const handleSaveAndAdvance = useCallback(() => {
    if (!userId) return;

    // Flush any content the editor holds that hasn't propagated to state yet
    const flushedGross =
      grossTemplateSelectorRef.current?.flushPendingContent?.() ?? null;

    // Snapshot current state so mutations fire with the right data
    const snapshot = { ...specimenUpdates };
    if (flushedGross != null && selectedSpecimenId) {
      const existing = snapshot[selectedSpecimenId] || {};
      snapshot[selectedSpecimenId] = { ...existing, gross: flushedGross };
    }

    const updates = Object.entries(snapshot).filter(
      ([, u]) => Object.keys(u).length > 0
    );

    // Fire all mutations — no await, no refetch.
    // Clear each specimen from local state only after its mutation confirms,
    // so navigating back mid-flight still shows the correct local data.
    updates.forEach(([specimenId, updateData]) => {
      updateSpecimenMutation.mutate(
        {
          caseId: caseId!,
          specimenId,
          data: { ...updateData, tissueDisposition: "entire_sample" },
        },
        {
          onSuccess: () => {
            setSpecimenUpdates((prev) => {
              const next = { ...prev };
              delete next[specimenId];
              return next;
            });
            refetchCase();
          },
          onError: () => {
            notifications.show({
              title: "Save Failed",
              message: "Changes could not be saved. Please try again.",
              color: "red",
            });
          },
        }
      );
    });

    // Advance to next specimen immediately
    if (selectedSpecimenId && caseData?.specimens) {
      const sortedSpecimens = [...caseData.specimens].sort((a: any, b: any) =>
        (a.number || "").localeCompare(b.number || "")
      );
      const currentIndex = sortedSpecimens.findIndex(
        (s: any) => s.id === selectedSpecimenId
      );
      if (currentIndex >= 0 && currentIndex < sortedSpecimens.length - 1) {
        setSelectedSpecimenId(sortedSpecimens[currentIndex + 1].id);
      }
    }
  }, [
    userId,
    specimenUpdates,
    selectedSpecimenId,
    caseData?.specimens,
    updateSpecimenMutation,
    caseId,
    refetchCase,
  ]);

  // Navigation and exit handlers
  const handleLeave = useCallback(async () => {
    closeCancelModal();
    if (caseId) {
      await releaseLockMutation.mutateAsync(caseId);
    }
    navigate("/grossing");
  }, [caseId, releaseLockMutation, navigate, closeCancelModal]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setPendingNavigation({ url: "/grossing" });
      openUnsavedModal();
    } else {
      openCancelModal();
    }
  }, [hasUnsavedChanges, openUnsavedModal, openCancelModal]);

  const navigateToPending = useCallback(async () => {
    if (caseId) {
      await releaseLockMutation.mutateAsync(caseId);
    }
    navigate(
      pendingNavigation?.url || "/grossing",
      pendingNavigation?.specimenLetter
        ? { state: { specimenLetter: pendingNavigation.specimenLetter } }
        : undefined
    );
  }, [caseId, releaseLockMutation, navigate, pendingNavigation]);

  const handleDiscardChanges = useCallback(() => {
    closeUnsavedModal();
    setSpecimenUpdates({});
    if (pendingNavigation) {
      navigateToPending();
    }
  }, [closeUnsavedModal, pendingNavigation, navigateToPending]);

  const handleSaveAndExit = useCallback(async () => {
    await handleSaveProgress(false); // Don't auto-advance since we're exiting
    closeUnsavedModal();
    if (pendingNavigation) {
      navigateToPending();
    }
  }, [
    handleSaveProgress,
    closeUnsavedModal,
    pendingNavigation,
    navigateToPending,
  ]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input or textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        // Only handle ESC in inputs
        if (e.key === "Escape") {
          target.blur();
        }
        return;
      }

      const specimens: Array<{ id: string }> = caseData?.specimens || [];
      const currentIndex: number = specimens.findIndex(
        (s) => s.id === selectedSpecimenId
      );

      switch (e.key) {
        case "Escape":
          // Exit to queue (with unsaved changes prompt if needed)
          handleCancel();
          e.preventDefault();
          break;
        case "ArrowUp":
          // Previous specimen — debounced to prevent rapid state desync on key-hold
          e.preventDefault();
          if (navDebounceRef.current) clearTimeout(navDebounceRef.current);
          navDebounceRef.current = setTimeout(() => {
            if (currentIndex > 0) {
              setSelectedSpecimenId(specimens[currentIndex - 1].id);
            }
          }, 150);
          break;
        case "ArrowDown": {
          // Next specimen — debounced to prevent rapid state desync on key-hold
          e.preventDefault();
          const nextIndex: number = currentIndex + 1;
          if (navDebounceRef.current) clearTimeout(navDebounceRef.current);
          navDebounceRef.current = setTimeout(() => {
            if (nextIndex < specimens.length) {
              setSelectedSpecimenId(specimens[nextIndex].id);
            }
          }, 150);
          break;
        }
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [caseData, selectedSpecimenId, handleCancel]);

  // Validate a single specimen - returns field-level errors
  const validateSingleSpecimen = (
    specimen: any
  ): { isValid: boolean; fieldErrors: Record<string, string> } => {
    const updates = specimenUpdates[specimen.id] || {};
    const gross =
      updates.gross !== undefined ? updates.gross : specimen.gross ?? "";
    const sourceId =
      updates.sourceId !== undefined ? updates.sourceId : specimen.sourceId;
    const collectionMethod =
      updates.collectionMethod !== undefined
        ? updates.collectionMethod
        : specimen.collectionMethod;

    const normalizedGross = normalizeGrossText(gross);

    const fieldErrors: Record<string, string> = {};

    if (!sourceId) {
      fieldErrors.source = "Source must be selected";
    }

    if (!collectionMethod) {
      fieldErrors.collectionMethod = "Collection method must be selected";
    }

    if (normalizedGross.length < 5) {
      fieldErrors.gross =
        "Gross description must have at least 5 meaningful characters";
    }

    if (hasUnresolvedTemplateVars(gross)) {
      fieldErrors.gross =
        "Gross description contains unfilled template variables";
    }

    return {
      isValid: Object.keys(fieldErrors).length === 0,
      fieldErrors,
    };
  };

  // Check if a specimen is complete (for badge display)
  const isSpecimenComplete = (specimen: any): boolean => {
    return validateSingleSpecimen(specimen).isValid;
  };

  // Validate all specimens before completion
  const validateSpecimens = (): {
    valid: boolean;
    errors: string[];
    firstInvalidSpecimenId: string | null;
    firstFieldError: string | null;
  } => {
    const specimens = caseData?.specimens || [];
    const errors: string[] = [];
    let firstInvalidSpecimenId: string | null = null;
    let firstFieldError: string | null = null;

    // Sort specimens to match displayed order
    const sortedSpecimens = [...specimens].sort((a: any, b: any) =>
      (a.number || "").localeCompare(b.number || "")
    );

    const fieldOrder = ["source", "collectionMethod", "gross"];

    sortedSpecimens.forEach((specimen: any) => {
      const { isValid, fieldErrors } = validateSingleSpecimen(specimen);
      if (!isValid) {
        // Track first invalid specimen
        if (!firstInvalidSpecimenId) {
          firstInvalidSpecimenId = specimen.id;
          // Get the first field error (in display order)
          const foundField = fieldOrder.find((field) => fieldErrors[field]);
          if (foundField) {
            firstFieldError = foundField;
          }
        }
        // Add errors for notification
        Object.entries(fieldErrors).forEach(([, message]) => {
          errors.push(`Specimen ${specimen.number}: ${message}`);
        });
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      firstInvalidSpecimenId,
      firstFieldError,
    };
  };

  // Scroll to a specific field error with exponential backoff to handle DOM not-yet-ready
  const scrollToFieldError = useCallback(
    (field: string) => {
      const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
        source: sourceFieldRef,
        collectionMethod: collectionMethodFieldRef,
        gross: grossFieldRef,
      };

      let attempts = 0;
      const maxAttempts = 10;

      const tryScroll = () => {
        const ref = refMap[field];
        if (ref?.current) {
          ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (attempts < maxAttempts) {
          attempts += 1;
          setTimeout(tryScroll, 100 + 50 * attempts); // 150ms, 200ms, 250ms ... up to ~600ms
        }
      };

      tryScroll();
    },
    [] // refs are stable across renders
  );

  const handleCompleteGrossing = async () => {
    if (!caseId || !userId) return;

    // Validate all specimens
    const { valid, errors, firstInvalidSpecimenId, firstFieldError } =
      validateSpecimens();
    if (!valid) {
      // Set field errors for highlighting
      if (firstInvalidSpecimenId && firstFieldError) {
        setFieldErrors({ [firstFieldError]: true });

        // If we need to switch specimens, do that first then scroll
        if (firstInvalidSpecimenId !== selectedSpecimenId) {
          setSelectedSpecimenId(firstInvalidSpecimenId);
        }
        // scrollToFieldError uses exponential backoff internally — safe to call immediately
        scrollToFieldError(firstFieldError);
      }

      notifications.show({
        title: "Validation Error",
        message:
          errors.slice(0, 3).join("\n") +
          (errors.length > 3 ? `\n...and ${errors.length - 3} more` : ""),
        color: "red",
        autoClose: 8000,
      });
      return;
    }

    // Clear any field errors
    setFieldErrors({});

    // Save any pending updates first (don't auto-advance since we're completing)
    if (Object.keys(specimenUpdates).length > 0) {
      await handleSaveProgress(false);
    }

    try {
      await completeGrossingMutation.mutateAsync(caseId);

      notifications.show({
        title: "Grossing Complete",
        message: "Case has been moved to the next workflow stage",
        color: "green",
      });

      // Always navigate back to the worklist
      navigate("/grossing");
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to complete grossing",
        color: "red",
      });
    }
  };

  // Navigation
  const currentCaseIndex = grossingQueue.findIndex((c: any) => c.id === caseId);
  const hasPreviousCase = currentCaseIndex > 0;
  const hasNextCase =
    currentCaseIndex >= 0 && currentCaseIndex < grossingQueue.length - 1;
  const previousCaseId = hasPreviousCase
    ? grossingQueue[currentCaseIndex - 1]?.id
    : null;
  const nextCaseId = hasNextCase
    ? grossingQueue[currentCaseIndex + 1]?.id
    : null;

  const navigateToCase = async (targetCaseId: string) => {
    if (caseId) {
      await releaseLockMutation.mutateAsync(caseId);
    }
    navigate(`/grossing/${targetCaseId}`);
  };

  // Auto-fill collection method from source default if current value is empty or not a valid option
  useEffect(() => {
    if (
      !caseData ||
      !selectedSpecimenId ||
      !collectionMethods.length ||
      !sources.length
    )
      return;

    const specimen = (caseData.specimens || []).find(
      (s: any) => s.id === selectedSpecimenId
    );
    if (!specimen) return;

    const sourceId =
      specimenUpdates[selectedSpecimenId]?.sourceId ?? specimen.sourceId ?? "";
    const collectionMethod =
      specimenUpdates[selectedSpecimenId]?.collectionMethod ??
      specimen.collectionMethod ??
      "";

    if (!sourceId) return;

    const isCollectionMethodValid =
      collectionMethod &&
      collectionMethods.some(
        (m: { value: string }) => m.value === collectionMethod
      );

    if (!isCollectionMethodValid) {
      const selectedSource = sources.find(
        (s: { value: string }) => s.value === sourceId
      );
      if (selectedSource?.defaultCollectionMethodName) {
        handleFieldChange(
          selectedSpecimenId,
          "collectionMethod",
          selectedSource.defaultCollectionMethodName
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecimenId, caseData, collectionMethods, sources]);

  // Loading state
  if (caseLoading) {
    return (
      <Box p="xl" maw={1200} mx="auto">
        <Group justify="center" p="xl">
          <Loader />
          <Text>Loading case...</Text>
        </Group>
      </Box>
    );
  }

  // Error state
  if (caseError || !caseData) {
    return (
      <Box p="xl" maw={1200} mx="auto">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          Failed to load case data. Please try again.
        </Alert>
        <Button
          mt="md"
          onClick={() => navigate("/grossing")}
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to Queue
        </Button>
      </Box>
    );
  }

  // Locked by another user state
  if (isLockedByOther) {
    return (
      <Box p="xl" maw={1200} mx="auto">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          title="Case Locked"
        >
          This case is currently being worked on by{" "}
          <strong>{lockData?.lockedByName || "another user"}</strong>. Please
          try again later or choose a different case from the queue.
        </Alert>
        <Group mt="md">
          <Button
            onClick={() => navigate("/grossing")}
            leftSection={<IconArrowLeft size={16} />}
          >
            Back to Queue
          </Button>
          {canForceRelease && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconLockOpen size={16} />}
              onClick={handleForceUnlock}
              loading={forceReleaseMutation.isPending}
            >
              Force Unlock
            </Button>
          )}
        </Group>
      </Box>
    );
  }

  // Whether a save is in flight — used to disable form inputs during save
  const isSaving = updateSpecimenMutation.isPending;

  // Sort specimens alphabetically by number (A, B, C, etc.)
  const specimens = [...(caseData.specimens || [])].sort((a: any, b: any) =>
    (a.number || "").localeCompare(b.number || "")
  );
  const grossedCount = specimens.filter((s: any) =>
    isSpecimenComplete(s)
  ).length;
  const totalCount = specimens.length;
  const selectedSpecimen = specimens.find(
    (s: any) => s.id === selectedSpecimenId
  );

  const MATERIAL_LABELS: Record<string, string> = {
    formalin_jar: "Formalin jar",
    block: "Block",
    slides_only: "Slides only",
    blood_tube: "Blood tube",
    urine_sample: "Urine sample",
    stool_collection: "Stool collection",
  };
  const currentMaterial = selectedSpecimen?.material ?? "";

  // Scroll to errors section handler
  const scrollToErrors = () => {
    errorsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <style>{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <Grid gutter={0} style={{ margin: "0 auto", maxWidth: "2200px" }}>
        {/* LEFT SIDEBAR - Case Summary, Notes, Errors */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 300 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              position: "relative",
              borderRight: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Box
              className="hide-scrollbar left-sidebar-scroll"
              style={{
                height: "100%",
                overflowY: "auto",
              }}
            >
              <Stack gap="md" p="md">
                {/* Case Summary */}
                <GrossingCaseSummary
                  caseId={caseId}
                  caseNumber={caseData.caseNumber}
                  externalCaseNumber={caseData.externalCaseNumber}
                  patient={caseData.patient}
                  procedure={caseData.procedure}
                  currentStatus={caseData.currentStatus}
                  isBlocked={caseData.isBlocked}
                  isRush={caseData.isRush}
                  isHighPriority={caseData.isHighPriority}
                  accessioningType={caseData.accessioningType}
                  pathologist={caseData.pathologist}
                  facility={caseData.facility}
                  specimenCount={totalCount}
                  hasPreviousCase={hasPreviousCase}
                  hasNextCase={hasNextCase}
                  onPreviousCase={() =>
                    previousCaseId && navigateToCase(previousCaseId)
                  }
                  onNextCase={() => nextCaseId && navigateToCase(nextCaseId)}
                />

                <GrossingRequisitionCard key={caseId} caseId={caseId!} />

                {/* Case Flags */}
                <CaseFlagsSection caseId={caseId || null} />

                {/* Case Notes */}
                <CaseNotesSection caseId={caseId || null} />

                {/* Case Comments */}
                <CaseCommentsSection caseId={caseId || null} />

                {/* Errors */}
                <div ref={errorsRef}>
                  <ErrorsCard caseId={caseId || null} />
                </div>
              </Stack>
            </Box>
            {/* Scroll indicator - only show when there's content to scroll */}
            {showScrollIndicator && (
              <Box
                onClick={() => {
                  const scrollContainer = document.querySelector(
                    ".left-sidebar-scroll"
                  );
                  if (scrollContainer) {
                    scrollContainer.scrollBy({ top: 150, behavior: "smooth" });
                  }
                }}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 36,
                  background:
                    "linear-gradient(to bottom, rgba(66, 133, 244, 0), rgba(66, 133, 244, 0.15))",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  paddingBottom: 6,
                }}
              >
                <Box
                  style={{
                    backgroundColor: "rgba(66, 133, 244, 0.2)",
                    borderRadius: 12,
                    padding: "2px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <IconChevronDown
                    size={14}
                    color="var(--mantine-color-blue-6)"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid.Col>

        {/* CENTER COLUMN - Specimen Tabs + Form */}
        <Grid.Col span={{ base: 12, lg: "auto" }} style={{ flex: 1 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Split: tabs strip (left) + form (right) */}
            <Box style={{ flex: 1, display: "flex", overflow: "hidden" }}>
              {/* Vertical Specimen Tab Strip */}
              <Box
                style={{
                  width: 180,
                  flexShrink: 0,
                  borderRight: "1px solid var(--mantine-color-gray-3)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "var(--mantine-spacing-sm)",
                  }}
                >
                  <Text size="xs" fw={600} c="dimmed" mb="xs" tt="uppercase">
                    Specimens
                  </Text>
                  <Stack gap={4}>
                    {specimens.map((specimen: any) => (
                      <UnstyledButton
                        key={specimen.id}
                        ref={(el: any) => {
                          specimenRefs.current[specimen.id] = el;
                        }}
                        onClick={() => setSelectedSpecimenId(specimen.id)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "var(--mantine-radius-sm)",
                          backgroundColor:
                            selectedSpecimenId === specimen.id
                              ? "var(--mantine-color-blue-0)"
                              : undefined,
                          borderLeft:
                            selectedSpecimenId === specimen.id
                              ? "3px solid var(--mantine-color-blue-5)"
                              : "3px solid transparent",
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Text
                            size="sm"
                            fw={selectedSpecimenId === specimen.id ? 600 : 400}
                          >
                            {specimen.number}
                          </Text>
                          {isSpecimenComplete(specimen) ? (
                            <IconCheck
                              size={13}
                              color="var(--mantine-color-green-6)"
                            />
                          ) : (
                            <Box
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "var(--mantine-color-gray-4)",
                              }}
                            />
                          )}
                        </Group>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {specimen.source?.name || "\u2014"}
                        </Text>
                      </UnstyledButton>
                    ))}
                  </Stack>
                </Box>
                {/* Progress at bottom of tab strip */}
                <Box
                  p="sm"
                  style={{
                    borderTop: "1px solid var(--mantine-color-gray-3)",
                  }}
                >
                  <Progress
                    value={(grossedCount / totalCount) * 100}
                    size="xs"
                    mb={4}
                  />
                  <Text size="xs" c="dimmed" ta="center">
                    {grossedCount}/{totalCount} done
                  </Text>
                </Box>
              </Box>

              {/* Form area */}
              <Box
                ref={formScrollRef}
                style={{ flex: 1, overflowY: "auto" }}
                p="md"
              >
                {/* Received External Blocks */}
                {allBlocks.filter((b: any) => b.isCompleted).length > 0 && (
                  <Stack gap="xs" mb="md">
                    <Text fw={500} size="sm" c="violet">
                      Received External Blocks
                    </Text>
                    {allBlocks
                      .filter((b: any) => b.isCompleted)
                      .map((block: any) => (
                        <ReceivedExternalPassThroughPanel
                          key={block.id}
                          itemLabel={block.blockLabel || block.id}
                          lastStickerPrintedAt={block.lastStickerPrintedAt}
                          onReprintClick={() => handleReprintSticker(block)}
                          onAcknowledge={() => {}}
                        />
                      ))}
                  </Stack>
                )}

                {/* Blocked Case Alert */}
                {caseData.isBlocked && (
                  <Alert
                    color="red"
                    icon={<IconAlertTriangle size={16} />}
                    mb="md"
                    title="Case Blocked"
                  >
                    This case has been blocked due to an error. You cannot
                    complete grossing until the error is resolved, but you can
                    continue working on specimen details.{" "}
                    <Anchor
                      td="underline"
                      onClick={scrollToErrors}
                      style={{ cursor: "pointer" }}
                    >
                      View errors
                    </Anchor>
                  </Alert>
                )}

                <Paper p="lg" withBorder>
                  {selectedSpecimen ? (
                    <Stack gap="md">
                      <Group justify="space-between" style={{ order: 1 }}>
                        <Group gap="sm">
                          <Title order={3}>
                            Specimen {selectedSpecimen.number}
                          </Title>
                          {currentMaterial && (
                            <Badge variant="light" color="gray" size="lg">
                              {MATERIAL_LABELS[currentMaterial] ||
                                currentMaterial}
                            </Badge>
                          )}
                          <SpecimenInsufficientTissueCheckbox
                            caseId={caseId!}
                            specimen={selectedSpecimen}
                            stage="grossing"
                            disabled={isSaving}
                          />
                        </Group>
                        {isSpecimenComplete(selectedSpecimen) && (
                          <Badge
                            color="green"
                            leftSection={<IconCheck size={12} />}
                          >
                            Complete
                          </Badge>
                        )}
                      </Group>

                      {/* DOM: GrossTemplate before Grid so Tab flows
                          GrossTemplate → Source → Subsite → CollMethod → FragCount.
                          CSS order keeps the visual layout unchanged. */}
                      <div ref={grossFieldRef} style={{ order: 3 }}>
                        <SpecimenGrossingFields
                          specimen={selectedSpecimen}
                          values={specimenUpdates[selectedSpecimenId] || {}}
                          onChange={(field, value) =>
                            handleFieldChange(selectedSpecimenId, field, value)
                          }
                          onFragmentCountChange={
                            handleSelectedSpecimenFragmentCountChange
                          }
                          disabled={isSaving}
                          fieldErrors={fieldErrors}
                          onClearError={(field) =>
                            setFieldErrors((prev) => ({
                              ...prev,
                              [field]: false,
                            }))
                          }
                          specimenCount={specimens.length}
                          grossTemplateSelectorRef={grossTemplateSelectorRef}
                          onGrossTabOut={() => saveButtonRef.current?.focus()}
                        />
                      </div>
                    </Stack>
                  ) : (
                    <Text c="dimmed" ta="center" p="xl">
                      Select a specimen to view details
                    </Text>
                  )}
                </Paper>
              </Box>
            </Box>

            {/* FIXED Footer - Back to Queue + Save + Complete */}
            <Box
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: "var(--mantine-spacing-md)",
                backgroundColor: "white",
              }}
            >
              <Group justify="space-between">
                <Button
                  variant="outline"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={handleCancel}
                  tabIndex={-1}
                >
                  Back to Queue
                </Button>
                <Group gap="sm">
                  {selectedSpecimen && (
                    <Button
                      ref={saveButtonRef}
                      variant="light"
                      leftSection={<IconDeviceFloppy size={16} />}
                      onClick={handleSaveAndAdvance}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Tab" && !e.shiftKey) {
                          const currentIndex = specimens.findIndex(
                            (s: any) => s.id === selectedSpecimenId
                          );
                          if (currentIndex >= 0) {
                            e.preventDefault();
                            handleSaveAndAdvance();
                          }
                        }
                      }}
                      loading={updateSpecimenMutation.isPending}
                    >
                      Save All
                    </Button>
                  )}
                  {["Pending Grossing", "Grossing"].includes(
                    caseData.currentStatus?.name
                  ) && (
                    <Button
                      color="green"
                      onClick={() => {
                        if (caseData.isBlocked) {
                          notifications.show({
                            title: "Case Blocked",
                            message:
                              "This case is blocked due to an error. Please resolve the error before completing grossing.",
                            color: "red",
                          });
                          // Scroll to top to show blocked alert
                          formScrollRef.current?.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                          return;
                        }
                        // Use full validation which includes navigation to error field
                        handleCompleteGrossing();
                      }}
                      loading={completeGrossingMutation.isPending}
                    >
                      Complete Grossing
                    </Button>
                  )}
                </Group>
              </Group>
            </Box>
          </Box>
        </Grid.Col>

        {/* RIGHT SIDEBAR - Scanner + Printer + Blocks */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 320 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            {/* Scrollable content */}
            <Box style={{ flex: 1, overflowY: "auto" }}>
              <Stack gap="md" p="md">
                {/* Backup Barcode Scanner */}
                {showLegacyScannerControls && (
                  <Paper withBorder p="md" radius="md">
                    <Stack gap="xs">
                      <Group gap="xs">
                        <IconBarcode size={18} />
                        <Text fw={600} size="sm">
                          Scan Cassette
                        </Text>
                      </Group>
                      <TextInput
                        ref={scanInputRef}
                        placeholder="Scan cassette barcode..."
                        value={scanInput}
                        onChange={(e) => {
                          setScanInput(e.target.value);
                          setScanError(null);
                        }}
                        onKeyDown={handleManualBarcodeScan}
                        size="sm"
                        error={scanError}
                      />
                      <Text size="xs" c="dimmed">
                        Scan to switch specimen or open a different case
                      </Text>
                    </Stack>
                  </Paper>
                )}

                {/* Printer Status */}
                <CassettePrinterStatusPanel />

                {/* Sticker Printer Status */}
                <StickerPrinterStatusPanel />

                {/* Blocks & Cassettes */}
                {selectedSpecimen && (
                  <BlockManagementCard
                    caseId={caseId!}
                    specimenId={selectedSpecimenId}
                    specimenLetter={selectedSpecimen.number}
                  />
                )}
              </Stack>
            </Box>

            {/* FIXED Footer - Print All */}
            <Box
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: "var(--mantine-spacing-md)",
                backgroundColor: "white",
              }}
            >
              <Button
                fullWidth
                variant="light"
                leftSection={<IconPrinter size={16} />}
                onClick={async () => {
                  if (!caseId || allBlocks.length === 0) {
                    notifications.show({
                      title: "No Blocks",
                      message: "There are no blocks to print",
                      color: "orange",
                    });
                    return;
                  }

                  try {
                    const blockIds = allBlocks.map((b: any) => b.id);
                    const result = await printLabelsMutation.mutateAsync({
                      caseId,
                      blockIds,
                    });

                    const sortedLabels = [...result.labels].sort((a, b) => {
                      const letterCompare = a.specimenLetter.localeCompare(
                        b.specimenLetter
                      );
                      if (letterCompare !== 0) return letterCompare;
                      return (
                        parseInt(a.blockNumber, 10) -
                        parseInt(b.blockNumber, 10)
                      );
                    });

                    await printLabels(sortedLabels);
                  } catch (err: any) {
                    notifications.show({
                      title: "Print Error",
                      message: err.message || "Failed to print labels",
                      color: "red",
                    });
                  }
                }}
                loading={printLabelsMutation.isPending}
                disabled={
                  allBlocks.length === 0 || printLabelsMutation.isPending
                }
              >
                Print All ({allBlocks.length})
              </Button>
            </Box>
          </Box>
        </Grid.Col>
      </Grid>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        opened={unsavedModalOpened}
        onClose={closeUnsavedModal}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveAndExit}
        isSaving={updateSpecimenMutation.isPending}
      />

      {/* Leave Confirmation Modal */}
      <Modal
        opened={cancelModalOpened}
        onClose={closeCancelModal}
        title="Leave Grossing?"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to leave grossing for case{" "}
            {caseData?.caseNumber}? The case lock will be released.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="outline" onClick={closeCancelModal}>
              Stay
            </Button>
            <Button
              color="red"
              onClick={handleLeave}
              loading={releaseLockMutation.isPending}
            >
              Leave
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
